/**
 * MemoryWallPage — /event/:slug/memory-wall
 *
 * Cinematic dark-wood memory board. Polaroids float on the surface with
 * subtle rotations + washi-tape pinning. Per Craig's spec, this is NOT
 * a Pinterest masonry — it's a growing physical memory board.
 *
 *   • dark wood texture (CSS-only layered gradients for v1 — Craig can
 *     drop a real /public/wood-texture.jpg later and we swap the
 *     background-image declaration)
 *   • warm fairy-light bloom across the top
 *   • polaroids positioned via server-side `wall.{x, y, rotation, scale}`
 *     so every client sees the same canvas
 *   • poll every 8s for additions (SSE in Phase 2)
 *   • newest snap softly highlighted on first appearance
 *   • empty-wall state stays elegant: "Be the first to leave a memory"
 *
 * Forbidden per spec: NO falling animations, NO physics, NO spinning
 * cards. The wall feels nostalgic, not gamified.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Plus, FilmStrip } from '@phosphor-icons/react';
import { useEventRecovery, useEventSession } from '../context/EventSessionProvider';
import { useAuth } from '../contexts/AuthContext';
import ReconnectingScreen from '../components/ReconnectingScreen';
import Polaroid from '../components/Polaroid';
import { useMemorySnapArrivals } from '../hooks/useMemorySnapArrivals';
import MemoryHeroMoment from '../components/MemoryHeroMoment';
import { resolveDashboardHref } from '../shared/guestSession';
import MemoryRecapGhostPill from '../components/MemoryRecapGhostPill';
import LatestChapterPill from '../components/LatestChapterPill';
import ShareWallPill from '../components/ShareWallPill';
import useScrollRestore from '../shared/navigation/useScrollRestore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLAROID_W = 200; // base render width on desktop; mobile scales

const MemoryWallPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const keepsakeGid = searchParams.get('keepsake') || null;
  const { event, status, retry } = useEventRecovery(slug);
  const { activeEvent } = useEventSession();
  const { user } = useAuth();

  // iter197 — Universal back-nav cinematic continuity. The wall is
  // a tall scroll surface; pressing back from a snap detail should
  // land the guest exactly where their eyes last rested.
  useScrollRestore(event?.event_id ? `memory_wall_${event.event_id}` : null);

  // Host detection — the host (organiser) sees the "latest chapter"
  // pill; guests don't. The keepsake pill takes priority when both
  // could surface (a host who arrived via the recap email's
  // ?keepsake= link would otherwise see two pills stacking, which
  // the brand brief forbids).
  const isHost = !!(user && event && user.user_id === event.user_id);
  const [keepsakeVisible, setKeepsakeVisible] = useState(false);
  // Chapter pill reports visibility upward too, so the share pill
  // can suppress itself while either of the higher-priority pills
  // is in the breathing area. Order of precedence:
  //   keepsake > chapter > share
  const [chapterVisible, setChapterVisible] = useState(false);

  // Shared arrivals hook — handles polling, new-snap detection, and
  // the stagger queue so multiple simultaneous arrivals play out as
  // distinct hero beats rather than a chaotic burst.
  const { allSnaps: snaps, heroSnap, isHeroActive } = useMemorySnapArrivals(event?.event_id, {
    intensity: 'wall',
    pollMs: 8_000,
    limit: 200,
  });

  // Fire view analytics once per event load.
  useEffect(() => {
    if (!event?.event_id) return;
    axios.post(`${API}/events/${event.event_id}/memory-snaps/track`, { type: 'memory_wall_viewed' }).catch(() => {});
  }, [event?.event_id]);

  // Compute total wall height — needed because we use absolute
  // positioning; without an explicit min-height the page would collapse.
  const wallMinHeight = useMemo(() => {
    if (!snaps.length) return 600;
    // Grid layout: roughly 6 columns per row, ~320px per row + padding.
    const rowCount = Math.ceil(snaps.length / 6);
    return Math.max(800, rowCount * 320 + 280);
  }, [snaps]);

  if (status === 'failed') return <ReconnectingScreen status="failed" onRetry={retry} slug={slug} activeEvent={activeEvent} dashboardHref="/" />;
  if (!event) return <ReconnectingScreen status={status === 'restoring' ? 'restoring' : 'reconnecting'} slug={slug} activeEvent={activeEvent} />;

  return (
    <div
      data-testid="memory-wall-page"
      style={{
        position: 'relative', minHeight: '100vh',
        // Rich dark-wood plank background — see /public/memory-wall-wood.jpg.
        // We layer warm vignettes on top so the bottom edge fades into
        // candle-glow rather than reading as a flat texture tile.
        backgroundImage: `
          radial-gradient(ellipse 90% 55% at 50% 0%, rgba(255,206,120,0.18), transparent 60%),
          radial-gradient(ellipse 70% 50% at 50% 100%, rgba(0,0,0,0.55), transparent 80%),
          url('/memory-wall-wood.jpg')
        `,
        backgroundSize: 'auto, auto, cover',
        backgroundPosition: 'top, bottom, center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed, fixed, fixed',
        color: '#fff',
        fontFamily: 'Inter, system-ui, sans-serif',
        overflowX: 'hidden',
      }}
    >
      {/* ── FAIRY LIGHT GARLAND ────────────────────────────────────
          Soft warm-gold bulbs strung across the top with a gentle
          drape (sine curve) and a thin black cable. The whole strip
          breathes via wallPulse so the lights feel alive at night.
          Per the reference image: bulbs are dense (~22 across), warm
          amber, with bright cores and soft halos. ── */}
      <svg aria-hidden viewBox="0 0 1200 140" preserveAspectRatio="none" style={{
        position: 'absolute', top: 0, left: 0, width: '100%', height: 140,
        pointerEvents: 'none', zIndex: 2,
      }}>
        <defs>
          <radialGradient id="bulbGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="#FFF1B8" stopOpacity="1" />
            <stop offset="40%" stopColor="#FFC761" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#FF8A2B" stopOpacity="0" />
          </radialGradient>
        </defs>
        {/* Cable drape — slight sine */}
        <path d="M 0 38 Q 300 96 600 56 T 1200 50" stroke="rgba(20,12,4,0.78)" strokeWidth="2" fill="none" />
        {/* Bulbs along the drape */}
        {[...Array(22)].map((_, i) => {
          const t = i / 21;
          const x = t * 1200;
          // Match the cubic Bézier-ish curve from the drape path
          const y = 38 + Math.sin(t * Math.PI * 2 + 0.3) * 12 + Math.sin(t * Math.PI * 4) * 6;
          const flick = (i % 3 === 0 ? '1.8s' : i % 2 === 0 ? '2.4s' : '3.1s');
          return (
            <g key={i}>
              <circle cx={x} cy={y + 14} r="32" fill="url(#bulbGlow)" opacity="0.55">
                <animate attributeName="opacity" values="0.45;0.7;0.45" dur={flick} repeatCount="indefinite" />
              </circle>
              <circle cx={x} cy={y + 14} r="5.5" fill="#FFE7A8" />
              <circle cx={x} cy={y + 14} r="2.5" fill="#FFF6D8" />
              <rect x={x - 1.2} y={y + 7} width="2.4" height="6" fill="rgba(30,20,8,0.85)" />
            </g>
          );
        })}
      </svg>

      {/* ── HEADER (top-left) ──────────────────────────────────────
          Large serif "Memory Wall" + subtitle, plus the
          "117 MEMORIES" counter pill below. Per reference: title
          left-aligned, not centered. The LIVE indicator sits on the
          opposite side (top-right). ── */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 30,
        padding: '28px 32px 18px',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16,
        background: 'linear-gradient(180deg, rgba(10,5,3,0.55) 0%, rgba(10,5,3,0) 100%)',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 14 }}>
          <div>
            <h1 style={{
              margin: 0,
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 'clamp(34px, 5vw, 56px)',
              fontWeight: 500,
              letterSpacing: '-0.02em',
              color: '#FFFFFF',
              textShadow: '0 2px 22px rgba(0,0,0,0.55), 0 0 36px rgba(255,206,120,0.18)',
            }}>
              Memory Wall <span style={{ color: 'rgba(212,175,94,0.85)', fontSize: '0.45em', verticalAlign: 'super' }}>✦</span>
            </h1>
            <p style={{ margin: '6px 0 0', fontSize: 13, color: 'rgba(255,235,210,0.72)', letterSpacing: '0.02em' }}>
              Memories from our amazing guests
            </p>
          </div>
          <div data-testid="memory-wall-counter-pill" style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            padding: '8px 16px 8px 12px', borderRadius: 999,
            background: 'rgba(20,12,6,0.55)',
            border: '1px solid rgba(212,175,94,0.32)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 6px 22px rgba(0,0,0,0.35), inset 0 1px 0 rgba(212,175,94,0.08)',
          }}>
            <span aria-hidden style={{
              width: 22, height: 22, borderRadius: '50%',
              background: 'radial-gradient(circle at 35% 30%, #F2D27C 0%, #C9963C 70%)',
              boxShadow: '0 0 14px rgba(212,175,94,0.55)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, color: '#3A1F08',
            }}>♥</span>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
              <span data-testid="memory-wall-count" style={{ fontSize: 18, fontWeight: 800, color: '#FFE7A8', letterSpacing: '-0.01em' }}>{snaps.length}</span>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.28em', color: 'rgba(255,235,210,0.55)', marginTop: 3 }}>MEMORIES</span>
            </div>
          </div>
        </div>
        <div data-testid="live-indicator" style={{
          marginTop: 8,
          display: 'inline-flex', alignItems: 'center', gap: 10,
          padding: '8px 16px', borderRadius: 999,
          background: 'rgba(20,12,6,0.55)',
          border: '1px solid rgba(212,175,94,0.20)',
          backdropFilter: 'blur(10px)',
          fontSize: 11, color: 'rgba(255,235,210,0.85)',
          fontWeight: 700, letterSpacing: '0.05em',
        }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            color: '#FFE7A8', letterSpacing: '0.22em', textTransform: 'uppercase',
          }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#5EE17B', boxShadow: '0 0 10px rgba(94,225,123,0.8)', animation: 'wallPulse 1.6s ease-in-out infinite' }} />
            Live
          </span>
          <span aria-hidden style={{ width: 1, height: 11, background: 'rgba(255,235,210,0.18)' }} />
          <span style={{ color: 'rgba(255,235,210,0.72)' }}>New memories are being added</span>
        </div>
      </div>

      {/* Ghost-pill — soft "a keepsake is waiting" affordance for guests
          who arrive via the recap email's "Revisit the wall" link.
          Renders nothing unless the backend confirms a recap was
          delivered to this specific guest_id. Positioned absolutely
          below the sticky header so it sits in the breathing room
          between the LIVE indicator and the first polaroid row. */}
      {keepsakeGid && event?.event_id && (
        <div style={{ position: 'absolute', top: 116, right: 24, zIndex: 31 }}>
          <MemoryRecapGhostPill
            eventId={event.event_id}
            guestId={keepsakeGid}
            onVisibilityChange={setKeepsakeVisible}
          />
        </div>
      )}

      {/* "Latest chapter has settled onto the shelf" pill — organiser-only,
          mutually exclusive with the keepsake pill above. Reads as
          discovered, not announced. Hands off to the archive route via
          a soft dim+settle transition so the surfaces feel physically
          adjacent rather than route-separated. */}
      {event?.event_id && (
        <div style={{ position: 'absolute', top: 116, right: 24, zIndex: 31 }}>
          <LatestChapterPill
            eventId={event.event_id}
            slug={slug}
            isHost={isHost}
            hidden={!!keepsakeGid || keepsakeVisible}
            onVisibilityChange={setChapterVisible}
          />
        </div>
      )}

      {/* "Share this wall ✦" pill — organiser-only, lowest priority in
          the mutual-exclusion chain so it never collides with a fresh
          chapter or keepsake. Positioned a tier below the higher pills
          (top: 168) so the breathing zone stays composed even when two
          surfaces happen to be active at once. */}
      {event?.event_id && (
        <div style={{ position: 'absolute', top: 168, right: 24, zIndex: 31 }}>
          <ShareWallPill
            slug={slug}
            isHost={isHost}
            hidden={!!keepsakeGid || keepsakeVisible || chapterVisible}
          />
        </div>
      )}

      {/* ── WALL SURFACE ────────────────────────────────────────────
          Grid layout — 3+ rows of polaroids organised left-to-right
          (per reference image). Each polaroid keeps its server-
          determined rotation for the "physical pinning" feel but the
          grid keeps the wall tidy at any density. ── */}
      <div style={{ position: 'relative', minHeight: wallMinHeight, padding: '18px 28px 220px' }} data-testid="wall-surface">
        {snaps.length === 0 && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} data-testid="memory-wall-empty">
            <div style={{ textAlign: 'center', maxWidth: 360, padding: 24 }}>
              <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.32em', textTransform: 'uppercase', color: 'rgba(212,175,94,0.85)', marginBottom: 14 }}>✦ Memory Wall</p>
              <h2 style={{ fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 500, letterSpacing: '-0.02em', margin: '0 0 12px', color: '#FFE7A8', textShadow: '0 0 28px rgba(212,175,94,0.35)' }}>
                Be the first to leave a memory
              </h2>
              <p style={{ fontSize: 14, color: 'rgba(255,235,210,0.7)', lineHeight: 1.6, marginBottom: 28 }}>
                Take a selfie, write a short note, and watch this wall fill up as the night unfolds.
              </p>
              <button
                type="button"
                onClick={() => navigate(`/event/${slug}/memory-snap`)}
                data-testid="empty-add-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 52, padding: '0 24px', borderRadius: 999, background: 'linear-gradient(135deg, #D4AF5E 0%, #B8923F 100%)', color: '#0F0B1A', border: 'none', fontWeight: 800, fontSize: 13, cursor: 'pointer', boxShadow: '0 8px 26px rgba(212,175,94,0.42)' }}
              ><Plus size={16} weight="bold" /> Add your Memory Snap</button>
              {(event?.features || []).includes('lens_notes') && (
                <div style={{ marginTop: 14 }}>
                  <button
                    type="button"
                    onClick={() => navigate(`/lens-note/${slug}`)}
                    data-testid="empty-lens-note-btn"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 999, background: 'transparent', color: '#D4AF5E', border: '1px solid rgba(212,175,94,0.55)', fontWeight: 700, fontSize: 12, letterSpacing: '0.06em', cursor: 'pointer' }}
                  >Leave a Lens Note ✦</button>
                </div>
              )}
              {(event?.features || []).includes('drop_one_word') && (
                <div style={{ marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={() => navigate(`/one-word/${slug}`)}
                    data-testid="empty-one-word-btn"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 999, background: 'transparent', color: '#D4AF5E', border: '1px solid rgba(212,175,94,0.55)', fontWeight: 700, fontSize: 12, letterSpacing: '0.06em', cursor: 'pointer' }}
                  >Drop One Word ✦</button>
                </div>
              )}
            </div>
          </div>
        )}

        {snaps.length > 0 && (
          <div
            data-testid="wall-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: '36px 24px',
              maxWidth: 1400,
              margin: '0 auto',
              alignItems: 'start',
              justifyItems: 'center',
            }}
          >
            {snaps.map((s) => {
              const rot = s.wall?.rotation ?? 0;
              const scale = s.wall?.scale ?? 1;
              const isHero = heroSnap?.snap_id === s.snap_id;
              const dimOpacity = isHero
                ? 0
                : (isHeroActive ? 0.94 : 1);
              // Lens Notes ✦ render as playable video cards in lieu of
              // the polaroid still — the handwriting is already baked
              // into the video so muted autoplay carries the message.
              if (s.kind === 'lens_note') {
                return (
                  <div
                    key={s.snap_id}
                    data-testid={`wall-lens-note-${s.lens_note_id || s.snap_id}`}
                    style={{
                      opacity: dimOpacity, transition: 'opacity 700ms ease-out',
                    }}
                  >
                    <div style={{
                      transform: `rotate(${rot}deg) scale(${scale})`,
                      transformOrigin: 'center top',
                      animation: 'wallSnapEnter 0.6s ease-out forwards',
                      position: 'relative', width: POLAROID_W, padding: '10px 10px 28px',
                      background: '#1a120a', borderRadius: 6,
                      boxShadow: '0 12px 26px rgba(0,0,0,0.55), 0 2px 6px rgba(0,0,0,0.4)',
                      border: '1px solid rgba(193,154,75,0.32)',
                    }}>
                      <video
                        src={s.lens_note_video_url}
                        poster={s.photo_url}
                        autoPlay muted loop playsInline
                        data-testid={`wall-lens-note-video-${s.lens_note_id || s.snap_id}`}
                        style={{ width: '100%', display: 'block', borderRadius: 3, background: '#000', aspectRatio: '9/16', objectFit: 'cover' }}
                      />
                      <p style={{
                        position: 'absolute', top: 14, left: 14,
                        margin: 0, padding: '3px 8px', fontSize: 9.5,
                        fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase',
                        color: '#1a0f04', background: 'rgba(193,154,75,0.92)',
                        borderRadius: 2,
                      }}>
                        Lens Note ✦
                      </p>
                    </div>
                  </div>
                );
              }
              return (
                // Outer wrapper holds the React-controlled dim opacity so
                // it isn't masked by the inner element's entrance keyframe
                // (CSS animations with `forwards` override inline values).
                <div
                  key={s.snap_id}
                  data-testid={`wall-snap-${s.snap_id}`}
                  style={{
                    opacity: dimOpacity,
                    transition: 'opacity 700ms ease-out',
                  }}
                >
                  <div
                    style={{
                      transform: `rotate(${rot}deg) scale(${scale})`,
                      transformOrigin: 'center top',
                      animation: 'wallSnapEnter 0.6s ease-out forwards',
                    }}
                  >
                    <Polaroid
                      photoUrl={s.photo_url}
                      message={s.message}
                      penColor={s.pen_hex}
                      seed={s.style_seed || 1}
                      width={POLAROID_W}
                      tape={s.wall?.tape || 'washi'}
                      showCorners
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Hero Moment overlay — when a new snap arrives the queue hook
            elevates it here for the cinematic entrance. */}
        {heroSnap && (
          <MemoryHeroMoment
            key={heroSnap.snap_id}
            snap={heroSnap}
            intensity="wall"
            anchor="top"
            testId={`wall-hero-${heroSnap.snap_id}`}
          />
        )}
      </div>

      {/* ── BOTTOM ACTION BAR ──────────────────────────────────────
          Gold-outlined centre CTA framed by two dark side pills —
          matches the reference image. Wider, calmer, more cinematic
          than the previous compact bar. ── */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        padding: '18px 24px calc(20px + env(safe-area-inset-bottom))',
        background: 'linear-gradient(180deg, rgba(10,5,3,0) 0%, rgba(10,5,3,0.86) 45%, rgba(10,5,3,0.96) 100%)',
        display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between',
        zIndex: 40,
      }}>
        <button
          type="button"
          onClick={() => navigate(resolveDashboardHref(slug))}
          data-testid="wall-dashboard-btn"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 10, height: 52, padding: '0 22px', borderRadius: 999, background: 'rgba(20,12,6,0.78)', border: '1px solid rgba(255,235,210,0.16)', color: 'rgba(255,235,210,0.92)', backdropFilter: 'blur(14px)', fontSize: 13, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.02em', boxShadow: '0 6px 18px rgba(0,0,0,0.4)' }}
        ><span aria-hidden style={{ fontSize: 14 }}>←</span> Back to Dashboard</button>
        <button
          type="button"
          onClick={() => navigate(`/event/${slug}/memory-snap`)}
          data-testid="wall-add-btn"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            height: 58, padding: '0 36px',
            borderRadius: 999,
            background: 'rgba(20,12,6,0.65)',
            border: '1.5px solid rgba(212,175,94,0.85)',
            color: '#FFE7A8',
            fontWeight: 800, fontSize: 15,
            letterSpacing: '0.01em',
            cursor: 'pointer',
            boxShadow: '0 0 32px rgba(212,175,94,0.35), inset 0 0 18px rgba(212,175,94,0.08)',
            backdropFilter: 'blur(14px)',
          }}
        >
          <Plus size={18} weight="bold" />
          Add your Memory Snap
          <span aria-hidden style={{ fontSize: 12, color: 'rgba(212,175,94,0.75)', marginLeft: 2 }}>✦</span>
        </button>
        <button
          type="button"
          onClick={() => navigate(`/event/${slug}/slideshow`)}
          data-testid="wall-slideshow-btn"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 10, height: 52, padding: '0 22px', borderRadius: 999, background: 'rgba(20,12,6,0.78)', border: '1px solid rgba(255,235,210,0.16)', color: 'rgba(255,235,210,0.92)', backdropFilter: 'blur(14px)', fontSize: 13, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.02em', boxShadow: '0 6px 18px rgba(0,0,0,0.4)' }}
        ><FilmStrip size={14} weight="bold" /> Slideshow</button>
        {(event?.features || []).includes('drop_one_word') && (
          <button
            type="button"
            onClick={() => navigate(`/one-word/${slug}`)}
            data-testid="wall-one-word-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 10, height: 52, padding: '0 22px', borderRadius: 999, background: 'rgba(20,12,6,0.78)', border: '1px solid rgba(193,154,75,0.42)', color: '#D4AF5E', backdropFilter: 'blur(14px)', fontSize: 13, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.02em', boxShadow: '0 6px 18px rgba(0,0,0,0.4)' }}
          >Drop One Word ✦</button>
        )}
      </div>

      <style>{`
        @keyframes wallPulse {
          0%, 100% { opacity: 0.6; transform: scale(0.94); }
          50% { opacity: 1; transform: scale(1.08); }
        }
        @keyframes wallSnapEnter {
          0% { opacity: 0; transform: scale(0.96); }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default MemoryWallPage;
